import './priceChecker.css';

import type { PriceCheckerDevice, PriceCheckerQuote } from '@pos/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { priceCheckerApi, PriceCheckerError } from './api';

const STORAGE = 'pos-price-checker-token';
const readToken = () => {
  try {
    return localStorage.getItem(STORAGE) ?? '';
  } catch {
    return '';
  }
};
const money = (value: number) =>
  (value / 100).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
type Screen =
  | { kind: 'idle' | 'loading' | 'not-found' | 'unavailable' | 'unauthorized' }
  | { kind: 'result'; quote: PriceCheckerQuote };

export function PriceCheckerView() {
  useEffect(() => {
    const previous = document.title;
    document.title = 'จุดเช็กราคา | แป้งสวย POS';
    return () => {
      document.title = previous;
    };
  }, []);
  const [token, setToken] = useState(readToken);
  const [code, setCode] = useState('');
  const [device, setDevice] = useState<PriceCheckerDevice | null>(null);
  const [screen, setScreen] = useState<Screen>({ kind: 'idle' });
  const [pairing, setPairing] = useState(false);
  const [pairError, setPairError] = useState('');
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);
  const recoveryAttempts = useRef(0);
  const pending = useRef<AbortController>();
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  const cancel = useCallback(() => {
    sequence.current++;
    pending.current?.abort();
    clearTimeout(resetTimer.current);
  }, []);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setDevice(null);
    setScreen({ kind: 'loading' });
    void priceCheckerApi
      .context(token, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setDevice(value);
        setScreen({ kind: 'idle' });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setScreen({
          kind:
            error instanceof PriceCheckerError &&
            [401, 403].includes(error.status)
              ? 'unauthorized'
              : 'unavailable',
        });
      });
    return () => {
      controller.abort();
      cancel();
    };
  }, [token, retry, cancel]);

  useEffect(() => {
    const offline = () => {
      cancel();
      setScreen({ kind: 'unavailable' });
    };
    const online = () => {
      cancel();
      setRetry((value) => value + 1);
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [cancel]);

  // Recover even when the API fails while the browser still considers itself
  // online (for example a Worker outage during kiosk startup).
  useEffect(() => {
    if (screen.kind === 'idle' || screen.kind === 'result')
      recoveryAttempts.current = 0;
    if (!token || screen.kind !== 'unavailable') return;
    const delay = Math.min(
      30000,
      5000 * 2 ** Math.min(recoveryAttempts.current, 3),
    );
    recoveryAttempts.current++;
    const timer = setTimeout(() => {
      cancel();
      setRetry((value) => value + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [token, screen.kind, cancel]);

  const scan = useCallback(
    async (barcode: string) => {
      cancel();
      const requestId = sequence.current;
      const controller = new AbortController();
      pending.current = controller;
      setScreen({ kind: 'loading' });
      try {
        const quote = await priceCheckerApi.lookup(
          token,
          barcode,
          controller.signal,
        );
        if (sequence.current !== requestId) return;
        setScreen({ kind: 'result', quote });
      } catch (error) {
        if (sequence.current !== requestId) return;
        const status = error instanceof PriceCheckerError ? error.status : 0;
        setScreen({
          kind: [401, 403].includes(status)
            ? 'unauthorized'
            : status === 404
              ? 'not-found'
              : 'unavailable',
        });
        if (status !== 404) return;
      }
      resetTimer.current = setTimeout(() => {
        if (sequence.current === requestId) setScreen({ kind: 'idle' });
      }, 15000);
    },
    [token, cancel],
  );

  useEffect(() => {
    if (!device || screen.kind === 'unauthorized') return;
    let buffer = '';
    let lastKeyAt = 0;
    let overflow = false;
    const keydown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing)
        return;
      if (Date.now() - lastKeyAt > 1000) {
        buffer = '';
        overflow = false;
      }
      lastKeyAt = Date.now();
      if (event.key === 'Enter') {
        if (!buffer && !overflow) return;
        event.preventDefault();
        if (!overflow && buffer.trim()) void scan(buffer.trim());
        buffer = '';
        overflow = false;
      } else if (event.key.length === 1) {
        event.preventDefault();
        if (buffer.length < 200) buffer += event.key;
        else overflow = true;
      }
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [device, screen.kind === 'unauthorized', scan]);

  async function pair(event: React.FormEvent) {
    event.preventDefault();
    setPairError('');
    setPairing(true);
    try {
      const value = code.trim();
      await priceCheckerApi.context(value);
      localStorage.setItem(STORAGE, value);
      setCode('');
      setToken(value);
    } catch (error) {
      setPairError(
        error instanceof PriceCheckerError && [401, 403].includes(error.status)
          ? 'รหัสติดตั้งไม่ถูกต้อง ถูกยกเลิก หรือหมดอายุ'
          : 'เชื่อมต่อหรือลงทะเบียนไม่สำเร็จ กรุณาลองอีกครั้งและอนุญาตการบันทึกข้อมูลในเบราว์เซอร์',
      );
    } finally {
      setPairing(false);
    }
  }

  if (!token)
    return (
      <main className="price-checker">
        <form className="pc-setup" onSubmit={pair}>
          <h1>ตั้งค่าจุดเช็กราคา</h1>
          <p>นำรหัสติดตั้งจากเมนูจุดเช็กราคาใน Dashboard มากรอกที่นี่</p>
          <label htmlFor="pc-code">รหัสติดตั้ง</label>
          <input
            id="pc-code"
            type="password"
            autoComplete="off"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
            maxLength={104}
          />
          <button disabled={pairing || !code.trim()}>
            {pairing ? 'กำลังเชื่อมต่อ…' : 'เปิดใช้งานเครื่องนี้'}
          </button>
          {pairError && <p role="alert">{pairError}</p>}
        </form>
      </main>
    );

  return (
    <main className="price-checker">
      <header>
        <strong>จุดเช็กราคา</strong>
        <span>{device?.branchName}</span>
      </header>
      <section className="pc-content" aria-live="polite" aria-atomic="true">
        {screen.kind === 'idle' && (
          <>
            <div className="pc-scan-symbol" aria-hidden="true">
              ▥
            </div>
            <h1>
              สแกนบาร์โค้ด
              <br />
              เพื่อเช็กราคา
            </h1>
            <p>ยื่นบาร์โค้ดสินค้าเข้าหาเครื่องสแกน</p>
            <div className="pc-arrow" aria-hidden="true">
              ↓
            </div>
          </>
        )}
        {screen.kind === 'loading' && (
          <>
            <h1>กำลังเช็กราคา…</h1>
            <p>กรุณารอสักครู่</p>
          </>
        )}
        {screen.kind === 'result' && (
          <>
            {screen.quote.imageUrl && (
              <img
                key={screen.quote.imageUrl}
                className="pc-product-image"
                src={screen.quote.imageUrl}
                alt=""
                referrerPolicy="no-referrer"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            )}
            <h1 className="pc-product-name">{screen.quote.productName}</h1>
            <p>ราคาสำหรับ 1 {screen.quote.unitName}</p>
            {screen.quote.price !== screen.quote.regularPrice && (
              <p>
                ราคาปกติ <del>฿{money(screen.quote.regularPrice)}</del>
              </p>
            )}
            <div className="pc-price">฿{money(screen.quote.price)}</div>
            <p className="pc-unit">ต่อ {screen.quote.unitName}</p>
            {screen.quote.promotions.length > 0 && (
              <ul className="pc-promotions">
                {screen.quote.promotions.map((promo, index) => (
                  <li key={index}>
                    <strong>{promo.name}</strong> — {promo.condition}
                    {promo.applied && ' · รวมส่วนลดนี้แล้ว'}
                  </li>
                ))}
              </ul>
            )}
            <p className="pc-hint">สแกนสินค้าชิ้นต่อไปได้เลย</p>
          </>
        )}
        {screen.kind === 'not-found' && (
          <>
            <h1>ไม่พบสินค้า</h1>
            <p>กรุณาสแกนอีกครั้ง หรือติดต่อพนักงาน</p>
          </>
        )}
        {screen.kind === 'unavailable' && (
          <>
            <h1>ไม่สามารถเช็กราคาได้ชั่วคราว</h1>
            <p>กรุณาติดต่อพนักงาน หรือลองสแกนอีกครั้ง</p>
            <button
              onClick={() => {
                cancel();
                setRetry((value) => value + 1);
              }}
            >
              ลองเชื่อมต่อใหม่
            </button>
          </>
        )}
        {screen.kind === 'unauthorized' && (
          <>
            <h1>เครื่องนี้ต้องเปิดใช้งานใหม่</h1>
            <p>กรุณาติดต่อพนักงานเพื่อตรวจสอบสิทธิ์เครื่อง</p>
            <button
              onClick={() => {
                cancel();
                try {
                  localStorage.removeItem(STORAGE);
                } catch {
                  /* Pairing will report storage errors. */
                }
                setToken('');
                setDevice(null);
              }}
            >
              กรอกรหัสติดตั้งใหม่
            </button>
          </>
        )}
      </section>
      <footer>
        <span>ราคาสำหรับการซื้อทั่วไป • เงื่อนไขอื่นตรวจสอบกับพนักงาน</span>
        <small>หมายเลขเครื่อง: {device?.deviceId ?? 'กำลังตรวจสอบ'}</small>
      </footer>
    </main>
  );
}
